import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { HighchartsThemeService } from '@frk/eds-components';
import cloneDeep from 'lodash/cloneDeep';
import { GermanCalculatorChartData } from '../../types/german-calculator.type';

@Component({
  selector: 'ft-gc-result-chart-data',
  templateUrl: './gc-result-chart-data.component.html',
  styleUrls: ['./gc-result-chart-data.component.scss'],
})
export class GcResultChartDataComponent implements OnInit, OnChanges {
  @Input() chartData: GermanCalculatorChartData;

  highcharts;
  chartOptions: Highcharts.Options;

  constructor(private highchartsTheme: HighchartsThemeService) {}

  ngOnInit(): void {
    import(/* webpackChunkName: "highcharts-async" */ 'highcharts').then(
      (highchartsModule) => {
        highchartsModule.setOptions(this.highchartsTheme.themeOptions);
        highchartsModule.setOptions({
          lang: {
            decimalPoint: ',',
            thousandsSep: '.',
          },
        });
        this.highcharts = highchartsModule;

        if (this.chartData) {
          this.setupChart(this.highcharts);
        }
      }
    );
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (!simpleChanges.chartData.isFirstChange()) {
      this.setupChart(this.highcharts);
    }
  }

  /**
   * Create/Update chart options and assign series.
   * @param highchartsModule dynamic imported highcharts module
   */
  setupChart(highchartsModule): void {
    this.createChartOptions(highchartsModule, this.chartData);

    this.chartOptions.series = this.chartData.series;

    if ('categories' in this.chartOptions.xAxis) {
      this.chartOptions.xAxis.categories = this.chartData.xAxisCategories;

      // cloned to trigger onChange
      this.chartOptions = cloneDeep(this.chartOptions);
    }
  }

  /**
   * Create initial highchart options.
   * @param highchartsModule highchart module imported
   * @paramchartData chart data object
   */
  private createChartOptions(
    highchartsModule,
    chartData: GermanCalculatorChartData
  ): void {
    this.chartOptions = {
      title: null,
      series: [],
      chart: {
        type: 'column',
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      tooltip: {
        formatter() {
          const decimalKey = 'decimals';
          return `${this.x} ${chartData.xAxisLabel}<br />
          ${highchartsModule.numberFormat(
            this.point.y,
            this.point[decimalKey]
          )}`;
        },
      },
      xAxis: {
        title: {
          text: this.chartData.xAxisLabel,
          style: {
            fontWeight: 'bold',
          },
        },
        categories: [],
        labels: {
          style: {
            color: this.highchartsTheme.themeFonts.colors.grey,
            fontSize: this.highchartsTheme.themeFonts.size.p,
            fontFamily: this.highchartsTheme.themeFonts.fontFamily,
          },
        },
      },
      yAxis: {
        gridLineDashStyle: 'LongDash',
        minTickInterval: 0.1,
        title: {
          text: this.chartData.yAxisLabel,
          style: {
            fontWeight: 'bold',
          },
        },
        labels: {
          useHTML: true,
          style: {
            color: this.highchartsTheme.themeFonts.colors.grey,
            fontSize: this.highchartsTheme.themeFonts.size.p,
            fontFamily: this.highchartsTheme.themeFonts.fontFamily,
          },
          formatter() {
            return `${highchartsModule.numberFormat(this.value, 1)}`;
          },
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          grouping: false,
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          events: {
            legendItemClick() {
              return false;
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          animation: {
            duration: 2000,
            easing: 'easeIn',
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
    };
  }
}
